import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminRole } from '../utils/roles';
import { getDashboardPath } from '../constants/siteNav';
import AuthShell, {
  AuthHeader,
  AuthFooterLink,
  AuthSubmitButton,
  AuthError,
  AuthBackHome
} from '../components/auth/AuthShell';
import AuthField from '../components/auth/AuthField';
import { authAPI } from '../lib/api';

function getSafeReturnPath(returnTo, user) {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return null;
  }
  if (returnTo === '/' || returnTo.startsWith('/?') || returnTo.startsWith('/#')) {
    return null;
  }
  if (returnTo.startsWith('/admin') && !isAdminRole(user?.role)) {
    return null;
  }
  return returnTo;
}

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const requestedReturnTo = searchParams.get('returnTo');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      };
      if (formData.phone.trim()) payload.phone = formData.phone.trim();

      const data = await authAPI.register(payload);

      if (data.success) {
        const user = data.data.user;
        login(user, data.data.token);
        const returnTo = getSafeReturnPath(requestedReturnTo, user);
        navigate(returnTo || getDashboardPath(user), { replace: true });
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell footer={<AuthBackHome />}>
      <AuthHeader
        title="Create account"
        subtitle="Join Niko Vibe and discover events"
      />

      <form onSubmit={handleSubmit} className="space-y-5">
        <AuthError message={error} />

        <AuthField
          id="name"
          label="Full name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter your full name"
          autoComplete="name"
          icon="user"
          required
        />

        <AuthField
          id="email"
          label="Email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter your email"
          autoComplete="email"
          icon="envelope"
          required
        />

        <AuthField
          id="phone"
          label="Phone (optional)"
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="Enter your phone number"
          autoComplete="tel"
          icon="phone"
        />

        <AuthField
          id="password"
          label="Password"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Create a password"
          autoComplete="new-password"
          icon="lock"
          required
        />

        <AuthField
          id="confirmPassword"
          label="Confirm password"
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Confirm your password"
          autoComplete="new-password"
          icon="lock"
          required
        />

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            required
            className="mt-1 rounded border-gray-300 text-gray-900 focus:ring-gray-400"
          />
          <span className="text-sm text-gray-500 leading-relaxed">
            I agree to the{' '}
            <Link to="/terms-of-service" className="font-medium text-gray-900 hover:underline">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy-policy" className="font-medium text-gray-900 hover:underline">
              Privacy Policy
            </Link>
          </span>
        </label>

        <AuthSubmitButton loading={loading} loadingText="Creating account…">
          Create account
        </AuthSubmitButton>

        <AuthFooterLink
          prompt="Already have an account?"
          linkText="Sign in"
          to={
            requestedReturnTo
              ? `/login?returnTo=${encodeURIComponent(requestedReturnTo)}`
              : '/login'
          }
        />
      </form>
    </AuthShell>
  );
};

export default RegisterPage;
